import { type TrackingData, TrackingEventsType } from 'types/tracking';
import { useTracking } from '../useTracking';
import {
  type UseComponentTracking,
  type TrackEmotionalSupportChatParams,
  type TrackResourceOpenParams,
  type TrackEmotionalSupportChatRatingParams,
  type TrackChatMessageSentParams,
  type TrackChatMessageUpdatedParams,
  type TrackChatMessagePinnedParams,
  type TrackChatAudioNoteRecordedParams,
  type TrackChatFileSharedParams,
} from './types';

export const useComponentTracking = (): UseComponentTracking => {
  const { track } = useTracking();

  const trackEvent = async <T extends Partial<TrackingData>>(
    eventType: TrackingEventsType,
    params?: T
  ): Promise<void> => {
    await track(eventType, params);
  };

  const trackResourceOpen = (params: TrackResourceOpenParams): Promise<void> =>
    trackEvent(TrackingEventsType.USER_OPENS_RESOURCE, {
      resourceId: params.resourceId,
      resourceType: params.resourceType,
      category: params.category,
    });

  const trackAppointmentConfirmed = (): Promise<void> =>
    trackEvent(TrackingEventsType.USER_CONFIRMS_APPOINTMENT);

  const trackEmotionalSupportChatStart = (params: TrackEmotionalSupportChatParams): Promise<void> =>
    trackEvent(TrackingEventsType.USER_STARTS_EMOTIONAL_SUPPORT_CHAT, {
      supporterId: params.supporterId ?? null,
    });

  const trackEmotionalSupportChatExit = (params: {
    supporterId: string | null;
    durationSec: string | number | null;
  }): Promise<void> =>
    trackEvent(TrackingEventsType.USER_EXIT_EMOTIONAL_SUPPORT_CHAT, {
      supporterId: params.supporterId ?? null,
      durationSec: String(params.durationSec),
    });

  const trackEmotionalSupportChatRating = (
    params: TrackEmotionalSupportChatRatingParams
  ): Promise<void> =>
    trackEvent(TrackingEventsType.USER_RATES_EMOTIONAL_SUPPORT_CHAT, {
      supporterId: params.supporterId ?? null,
      ratingValue: String(params.ratingValue),
      comment: params.comment ?? null,
    });

  const trackChatMessageSent = (params: TrackChatMessageSentParams): Promise<void> =>
    trackEvent(TrackingEventsType.CHAT_MESSAGE_SENT, params);

  const trackChatMessageUpdated = (params: TrackChatMessageUpdatedParams): Promise<void> =>
    trackEvent(TrackingEventsType.CHAT_MESSAGE_UPDATED, params);

  const trackChatMessagePinned = (params: TrackChatMessagePinnedParams): Promise<void> =>
    trackEvent(TrackingEventsType.CHAT_MESSAGE_PINNED, params);

  const trackChatFileShared = (params: TrackChatFileSharedParams): Promise<void> =>
    trackEvent(TrackingEventsType.CHAT_FILE_SHARED, params);

  const trackChatAudioNoteRecorded = (params: TrackChatAudioNoteRecordedParams): Promise<void> =>
    trackEvent(TrackingEventsType.CHAT_AUDIO_NOTE_RECORDED, params);

  return {
    trackResourceOpen,
    trackEmotionalSupportChatStart,
    trackAppointmentConfirmed,
    trackEmotionalSupportChatExit,
    trackEmotionalSupportChatRating,
    trackChatMessageSent,
    trackChatMessageUpdated,
    trackChatMessagePinned,
    trackChatFileShared,
    trackChatAudioNoteRecorded,
  };
};
